import { type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { AnimatedNumber } from "./AnimatedNumber";

export type MetricTone = "up" | "warn" | "danger" | "neutral";

interface MetricCardBaseProps {
  label: string;
  /** Secondary line (delta, trend, qualifier). */
  sub?: string;
  subTone?: MetricTone;
  /** Tertiary detail, smaller and always muted. */
  detail?: string;
  /** Trailing element rendered in the top-right — e.g. a badge or icon. */
  trailing?: ReactNode;
  className?: string;
}

interface StaticValueProps extends MetricCardBaseProps {
  /** Pre-formatted value string. Not animated. */
  value: string;
  numericValue?: never;
  formatValue?: never;
}

interface AnimatedValueProps extends MetricCardBaseProps {
  /** Numeric value; will smoothly tween from the previous value on change. */
  numericValue: number;
  /** Format the in-flight numeric into the displayed string. */
  formatValue: (v: number) => string;
  value?: never;
}

type MetricCardProps = StaticValueProps | AnimatedValueProps;

const toneClasses: Record<MetricTone, string> = {
  up: "text-[#3B6D11]",
  warn: "text-[var(--color-warning)]",
  danger: "text-[var(--color-error)]",
  neutral: "text-[var(--color-text-tertiary)]",
};

/**
 * KPI tile used across dashboards.
 *
 * Prefer the `numericValue` + `formatValue` form when the value can change at
 * runtime (filter updates, refresh) — the primary number tweens smoothly.
 * Use the plain `value` string form for values that are never updated.
 */
export function MetricCard(props: MetricCardProps) {
  const { label, sub, subTone = "neutral", detail, trailing, className } = props;

  return (
    <div
      className={cn(
        "bg-white border border-[var(--color-border-light)] rounded-[10px] px-4 py-3.5",
        "flex flex-col gap-1.5 transition-shadow hover:shadow-sm",
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] font-medium">
          {label}
        </div>
        {trailing}
      </div>
      {"numericValue" in props && props.numericValue !== undefined ? (
        <AnimatedNumber
          value={props.numericValue}
          format={props.formatValue}
          className="text-[22px] font-semibold leading-tight tracking-tight text-[var(--color-text-primary)]"
          ariaLabel={props.formatValue(props.numericValue)}
        />
      ) : (
        <div
          className="text-[22px] font-semibold leading-tight tracking-tight text-[var(--color-text-primary)]"
          style={{ fontFeatureSettings: '"tnum"' }}
        >
          {"value" in props ? props.value : ""}
        </div>
      )}
      {sub && <div className={cn("text-[11px]", toneClasses[subTone])}>{sub}</div>}
      {detail && <div className="text-[10px] text-[var(--color-text-tertiary)]">{detail}</div>}
    </div>
  );
}
